"use client";

import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, CARD, LABEL } from "@/components/tool-kit/styles";
import { CompactTextArea } from "@/components/tools/academic-correspondence/kit";
import {
  type ReviewerComment,
  STANCES,
  type Stance,
} from "@/lib/tool-kit/correspondence/response-to-reviewers";

/*
 * One reviewer comment in the Response to Reviewers Generator: the comment as split
 * (editable, mergeable, removable), the stance, and the fields that stance needs.
 * Every word comes from the widget's strings, typed here as the subset the card reads.
 */

export interface CommentCardStrings {
  commentTextLabel: string;
  mergeUp: string;
  remove: string;
  stanceLabel: string;
  stanceOptions: Readonly<Record<Stance, string>>;
  changeLabel: string;
  changeOptionalLabel: string;
  changePlaceholder: string;
  rationaleLabel: string;
  rationalePlaceholder: string;
  pageLabel: string;
  pagePlaceholder: string;
  linesLabel: string;
  linesPlaceholder: string;
  explainedHint: string;
  newTextLabel: string;
  newTextPlaceholder: string;
}

type Patch = Partial<Omit<ReviewerComment, "id">>;

const STANCE_KEYS: readonly Stance[] = STANCES;

/** What to ask for: a change for agreed points, reasoning for declined ones. */
function StanceFields({
  comment,
  onChange,
  strings,
}: {
  comment: ReviewerComment;
  onChange: (patch: Patch) => void;
  strings: CommentCardStrings;
}) {
  const { stance } = comment;
  const revised = stance === "agree" || stance === "partial";
  const reasoned = stance === "partial" || stance === "disagree";
  const id = (field: string) => `rtr-${comment.id}-${field}`;
  return (
    <>
      {revised || stance === "thanks" ? (
        <CompactTextArea
          id={id("change")}
          label={revised ? strings.changeLabel : strings.changeOptionalLabel}
          onChange={(change) => onChange({ change })}
          placeholder={strings.changePlaceholder}
          rows={2}
          value={comment.change}
        />
      ) : null}
      {reasoned ? (
        <CompactTextArea
          id={id("rationale")}
          label={strings.rationaleLabel}
          onChange={(rationale) => onChange({ rationale })}
          placeholder={strings.rationalePlaceholder}
          rows={2}
          value={comment.rationale}
        />
      ) : null}
      {stance === "thanks" ? null : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={stance === "disagree" ? strings.explainedHint : undefined}
            id={id("page")}
            label={strings.pageLabel}
            onChange={(page) => onChange({ page })}
            placeholder={strings.pagePlaceholder}
            value={comment.page}
          />
          <TextField
            id={id("lines")}
            label={strings.linesLabel}
            onChange={(lines) => onChange({ lines })}
            placeholder={strings.linesPlaceholder}
            value={comment.lines}
          />
        </div>
      )}
      {revised ? (
        <CompactTextArea
          id={id("new-text")}
          label={strings.newTextLabel}
          onChange={(newText) => onChange({ newText })}
          placeholder={strings.newTextPlaceholder}
          rows={2}
          value={comment.newText}
        />
      ) : null}
    </>
  );
}

export function ReviewerCommentCard({
  comment,
  label,
  canMerge,
  onChange,
  onMerge,
  onRemove,
  strings,
}: {
  comment: ReviewerComment;
  /** "Reviewer 1 · Comment 1.2" */
  label: string;
  canMerge: boolean;
  onChange: (patch: Patch) => void;
  onMerge: () => void;
  onRemove: () => void;
  strings: CommentCardStrings;
}) {
  return (
    <li className={`${CARD} space-y-4`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={LABEL}>
          {label}
          {comment.section === "" ? null : (
            <span className="font-normal text-muted-foreground">
              {" "}
              ({comment.section})
            </span>
          )}
        </p>
        <div className="flex gap-2">
          <button
            className={BUTTON_SMALL}
            disabled={!canMerge}
            onClick={onMerge}
            type="button"
          >
            {strings.mergeUp}
          </button>
          <button className={BUTTON_SMALL} onClick={onRemove} type="button">
            {strings.remove}
          </button>
        </div>
      </div>
      <CompactTextArea
        id={`rtr-${comment.id}-text`}
        label={strings.commentTextLabel}
        onChange={(text) => onChange({ text })}
        rows={3}
        value={comment.text}
      />
      <SelectField
        id={`rtr-${comment.id}-stance`}
        label={strings.stanceLabel}
        onChange={(stance: Stance) => onChange({ stance })}
        options={STANCE_KEYS.map((key) => ({
          value: key,
          label: strings.stanceOptions[key],
        }))}
        value={comment.stance}
      />
      <StanceFields comment={comment} onChange={onChange} strings={strings} />
    </li>
  );
}
